import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import * as api from '../adminApi'
import { useLatestLoad } from '../../hooks/useLatestLoad'
import { ApiError } from '../../core/api/http'
import { errorMessage } from '../../core/errors'
import { weekOf } from './agendaRules'
import type {
  AgendaAbsenceRequest,
  AgendaAppointment,
  AgendaBlock,
  AgendaBlockRequest,
  AgendaCompleteRequest,
  AgendaMoveRequest,
  AgendaNewRequest,
  AgendaRange,
  AgendaSeriesRequest,
} from '../../core/types/agenda'

/**
 * De agenda van één week: ophalen, en elke actie wachten op de server (pessimistisch).
 * Bewust niet optimistisch: een afspraak die op het scherm staat maar niet bij de server,
 * betekent een klant voor een dichte deur. Eén actie per afspraak tegelijk; acties op
 * verschillende afspraken lopen gewoon naast elkaar.
 */
export function useAgenda(date: string) {
  const { from, to } = weekOf(date)
  const load = useCallback((opts: { signal?: AbortSignal }) => api.fetchAgenda(from, to, opts), [from, to])
  const { data, setData, status, reload, refresh } = useLatestLoad<AgendaRange>(load)
  const running = useRef(new Set<string>())
  const [busy, setBusy] = useState<string[]>([])

  const putAppointment = useCallback(
    (a: AgendaAppointment) =>
      setData((d) =>
        d && {
          ...d,
          appointments: d.appointments.some((x) => x.id === a.id) ? d.appointments.map((x) => (x.id === a.id ? a : x)) : [...d.appointments, a],
        },
      ),
    [setData],
  )

  /** `null` = mislukt (de melding is al getoond). Bij 404/409 de week opnieuw ophalen: iemand anders was sneller. */
  const run = useCallback(
    async <T>(key: string, fn: () => Promise<T>, failTitle: string): Promise<T | null> => {
      if (running.current.has(key)) return null
      running.current.add(key)
      setBusy([...running.current])
      try {
        return await fn()
      } catch (err) {
        // Al afgerond of geannuleerd: vaak een herhaling na een weggevallen antwoord (de eerste poging
        // lukte wél). Geen rode "lukte niet", maar zeggen wat er is; de ververste week toont de stand.
        if (err instanceof ApiError && err.code === 'appointment_closed') toast.info('Deze afspraak was al afgerond of geannuleerd', { description: 'De agenda is bijgewerkt.' })
        else toast.error(failTitle, { description: errorMessage(err) })
        if (err instanceof ApiError && (err.status === 404 || err.status === 409)) refresh()
        return null
      } finally {
        running.current.delete(key)
        setBusy([...running.current])
      }
    },
    [refresh],
  )

  const withAppointment = useCallback(
    async (id: string, fn: () => Promise<AgendaAppointment>, failTitle: string, done?: string) => {
      const a = await run(id, fn, failTitle)
      if (a) {
        putAppointment(a)
        if (done) toast.success(done)
      }
      return a
    },
    [run, putAppointment],
  )

  const add = useCallback(
    async (req: AgendaNewRequest) => {
      const a = await run(`new-${req.requestId}`, () => api.addAgendaAppointment(req), 'Inplannen lukte niet')
      if (a) {
        putAppointment(a)
        toast.success('Ingepland', { description: `${a.customer.name} krijgt een bevestiging met agenda-uitnodiging.` })
      }
      return a
    },
    [run, putAppointment],
  )

  /**
   * Een reeks: `null` = mislukt (melding getoond). Wat wel lukte, staat meteen in de week; de
   * overgeslagen datums laat het formulier zien, want die moet Sarah zelf nog oplossen.
   */
  const addSeries = useCallback(
    async (req: AgendaSeriesRequest) => {
      const result = await run(`new-${req.requestId}`, () => api.addAgendaSeries(req), 'Inplannen lukte niet')
      if (result) {
        result.created.forEach(putAppointment)
        if (result.created.length > 0) {
          toast.success(`${result.created.length} afspraken ingepland`, {
            description: `${result.created[0].customer.name} krijgt één bevestiging met alle datums.`,
          })
        }
      }
      return result
    },
    [run, putAppointment],
  )

  const addBlock = useCallback(
    async (req: AgendaBlockRequest) => {
      const b = await run(`block-${req.requestId}`, () => api.addAgendaBlock(req), 'Blokkeren lukte niet')
      if (b) setData((d) => d && { ...d, blocks: [...d.blocks.filter((x) => x.id !== b.id), b] })
      return b
    },
    [run, setData],
  )

  const removeBlock = useCallback(
    async (block: AgendaBlock) => {
      const ok = await run(block.id, () => api.removeAgendaBlock(block.id).then(() => true), 'Weghalen lukte niet')
      if (ok) setData((d) => d && { ...d, blocks: d.blocks.filter((x) => x.id !== block.id) })
      return Boolean(ok)
    },
    [run, setData],
  )

  /** "Mila is ziek": `null` = mislukt (melding getoond). Daarna de week opnieuw ophalen. */
  const reportAbsence = useCallback(
    async (req: AgendaAbsenceRequest) => {
      const result = await run(`absence-${req.date}`, () => api.reportAbsence(req), 'Afwezig melden lukte niet')
      if (result) refresh()
      return result
    },
    [run, refresh],
  )

  return {
    range: data,
    reportAbsence,
    status,
    reload,
    refresh,
    isBusy: (id: string) => busy.includes(id),
    add,
    addSeries,
    addBlock,
    removeBlock,
    move: (id: string, req: AgendaMoveRequest) =>
      withAppointment(id, () => api.moveAgendaAppointment(id, req), 'Verzetten lukte niet', 'Verzet. De klant krijgt bericht met de nieuwe tijd.'),
    confirm: (id: string) => withAppointment(id, () => api.confirmAgendaAppointment(id), 'Bevestigen lukte niet', 'Bevestigd. De klant krijgt een mail met agenda-uitnodiging.'),
    arrive: (id: string) => withAppointment(id, () => api.arriveAgendaAppointment(id), 'Opslaan lukte niet'),
    complete: (id: string, req: AgendaCompleteRequest) =>
      withAppointment(id, () => api.completeAgendaAppointment(id, req), 'Afronden lukte niet', 'Afgerond en bij de klant vastgelegd.'),
    noShow: (id: string) => withAppointment(id, () => api.noShowAgendaAppointment(id), 'Opslaan lukte niet', 'Vastgelegd als niet gekomen.'),
    cancel: (id: string) => withAppointment(id, () => api.cancelAgendaAppointment(id), 'Annuleren lukte niet', 'Geannuleerd. De klant krijgt bericht.'),
  }
}

export type AgendaControl = ReturnType<typeof useAgenda>
